import { useCallback, useSyncExternalStore } from "react";
import { useAuth } from "@/auth/AuthContext";
import { useFeatureFlag } from "@/hooks/useFeatureFlags.tsx";
import { isWatchKnown, useVulnerabilityWatch } from "@/hooks/useVulnerabilityWatch.ts";
import type { WatchVulnerability } from "@/types";

const EMPTY: ReadonlySet<string> = new Set();
const sessionSeen = new Map<string, Set<string>>();
const snapshots = new Map<string, { raw: string | null; session: number; set: ReadonlySet<string> }>();
const listeners = new Set<() => void>();
let sessionChanges = 0;

function storageKey(companyId: string, userId: string): string {
  return `vulnWatchSeen:${companyId}:${userId}`;
}

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function parseStored(raw: string | null): string[] {
  if (raw === null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

/** The stored ids plus those added this session, so a failing store still clears the number until reload. */
function snapshot(key: string): ReadonlySet<string> {
  const raw = readStored(key);
  const cached = snapshots.get(key);
  if (cached && cached.raw === raw && cached.session === sessionChanges) return cached.set;
  const set = new Set([...parseStored(raw), ...(sessionSeen.get(key) ?? [])]);
  snapshots.set(key, { raw, session: sessionChanges, set });
  return set;
}

function markSeen(key: string, cves: readonly string[]): void {
  const current = snapshot(key);
  const added = cves.filter((cve) => !current.has(cve));
  if (added.length === 0) return;
  const session = sessionSeen.get(key) ?? new Set<string>();
  added.forEach((cve) => session.add(cve));
  sessionSeen.set(key, session);
  sessionChanges += 1;
  try {
    localStorage.setItem(key, JSON.stringify([...current, ...added]));
  } catch {
    // The in-memory set above still holds them for this session.
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The CVEs this user has seen on the Vulnerability Watch tab; null while the company or user is not known. */
export function useVulnerabilityWatchSeen() {
  const { user, effectiveCompanyId } = useAuth();
  const key = effectiveCompanyId && user?.user_id ? storageKey(effectiveCompanyId, user.user_id) : null;
  const seen = useSyncExternalStore(subscribe, () => (key ? snapshot(key) : EMPTY));
  const mark = useCallback((cves: readonly string[]) => { if (key) markSeen(key, cves); }, [key]);
  return { seen: key ? seen : null, markSeen: mark };
}

/** Distinct CVEs that affect the version, or are not fixed and not seen yet. */
export function newOrAffectingCount(vulnerabilities: readonly WatchVulnerability[], seen: ReadonlySet<string>): number {
  const counted = new Set<string>();
  for (const v of vulnerabilities) {
    if (v.status === "affects" || (v.status !== "fixed" && !seen.has(v.cve))) counted.add(v.cve);
  }
  return counted.size;
}

/** The number next to Vulnerability Watch in the navigation; 0 whenever it cannot be known. */
export function useVulnerabilityWatchBadgeCount(): number {
  const enabled = useFeatureFlag("vulnWatch");
  const { data, error } = useVulnerabilityWatch(enabled);
  const { seen } = useVulnerabilityWatchSeen();
  if (!enabled || error || !data || !seen || !isWatchKnown(data)) return 0;
  return newOrAffectingCount(data.vulnerabilities, seen);
}
